import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, LogOut, Filter, X, Car, ChevronDown, Loader2, Bookmark, ShoppingBag, Headphones, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { CarCard } from "@/components/CarCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 24;

export default function Console() {
  const { user, logout } = useAuth();

  const [cars, setCars] = useState([]);
  const [meta, setMeta] = useState({ total: 0, countries: [], brands: [] });
  const [q, setQ] = useState("");
  const [country, setCountry] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [brandQuery, setBrandQuery] = useState("");
  const [brandOpen, setBrandOpen] = useState(false);

  const sentinelRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/cars/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  // Reset when filters change
  useEffect(() => {
    setLoading(true);
    setReachedEnd(false);
    const params = { limit: PAGE, skip: 0 };
    if (q.trim()) params.q = q.trim();
    if (country) params.country = country;
    if (brand) params.brand = brand;

    const t = setTimeout(() => {
      axios
        .get(`${API}/cars`, { params })
        .then((r) => {
          setCars(r.data);
          if (r.data.length < PAGE) setReachedEnd(true);
        })
        .catch(() => setCars([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, country, brand]);

  // Load more
  const loadMore = async () => {
    if (loading || loadingMore || reachedEnd) return;
    setLoadingMore(true);
    const params = { limit: PAGE, skip: cars.length };
    if (q.trim()) params.q = q.trim();
    if (country) params.country = country;
    if (brand) params.brand = brand;
    try {
      const { data } = await axios.get(`${API}/cars`, { params });
      setCars((prev) => [...prev, ...data]);
      if (data.length < PAGE) setReachedEnd(true);
    } catch {
      setReachedEnd(true);
    } finally {
      setLoadingMore(false);
    }
  };

  // Intersection observer for infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "600px" }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, loading, reachedEnd, q, country, brand]);

  const clearFilters = () => {
    setQ("");
    setCountry(null);
    setBrand(null);
    setBrandQuery("");
  };

  const initials = useMemo(
    () =>
      (user?.name || user?.email || "?")
        .split(/[\s@]/)
        .filter(Boolean)
        .map((s) => s[0]?.toUpperCase())
        .slice(0, 2)
        .join(""),
    [user],
  );

  const filteredBrandOptions = useMemo(() => {
    const q_ = brandQuery.trim().toLowerCase();
    if (!q_) return meta.brands.slice(0, 200);
    return meta.brands.filter((b) => b.toLowerCase().includes(q_)).slice(0, 200);
  }, [brandQuery, meta.brands]);

  const topCountries = meta.countries.slice(0, 12);
  const shownCountries = showAllCountries ? meta.countries : topCountries;

  return (
    <div
      className="min-h-screen bg-void text-white relative"
      style={{ overflowX: "clip" }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at top, #000 20%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at top, #000 20%, transparent 75%)",
        }}
      />

      <header
        data-testid="console-header"
        className="sticky top-0 z-30 border-b border-white/10 bg-void/85 backdrop-blur-xl"
      >
        <div className="mx-auto max-w-[1500px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="console-logo" className="flex items-center gap-3">
            <BrandLogo compact />
            <span className="hidden md:inline text-sm text-mymech-grey ml-1">
              / console
            </span>
          </Link>

          <div className="flex items-center gap-3">
          <Link
              to="/console/marketplace"
              className="hidden md:inline-flex items-center gap-2 h-9 px-4 border border-white/15 hover:border-cyan-electric hover:text-cyan-electric text-white/70 font-mono-tech text-[10px] uppercase tracking-widest transition-all"
            >
              <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.5} />
              marketplace
            </Link>
            {user?.role === "admin" && (
              <Link
                to="/console/admin"
                className="inline-flex items-center gap-2 h-9 px-4 border border-mclaren/50 bg-mclaren/10 text-mclaren font-mono-tech text-[10px] uppercase tracking-widest hover:bg-mclaren/20 transition-all"
              >
                <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />
                admin
              </Link>
            )}
            {["admin", "sales"].includes(user?.role) && (
              <Link
                to="/console/sales"
                className="inline-flex items-center gap-2 h-9 px-4 border border-cyan-electric/50 bg-cyan-electric/10 text-cyan-electric font-mono-tech text-[10px] uppercase tracking-widest hover:bg-cyan-electric/20 transition-all"
              >
                <Headphones className="h-3.5 w-3.5" strokeWidth={1.5} />
                sales desk
              </Link>
            )}
          <Link
              to="/console/garage"
              data-testid="console-garage-link"
              className="hidden md:inline-flex items-center gap-2 h-9 px-4 border border-white/15 hover:border-cyan-electric hover:text-cyan-electric text-white/70 font-mono-tech text-[10px] uppercase tracking-widest transition-all"
            >
              <Bookmark className="h-3.5 w-3.5" strokeWidth={1.5} />
              garage
            </Link>
            <Link
              to="/console/register"
              className="hidden md:inline-flex items-center gap-2 h-9 px-4 bg-mclaren text-void font-mono-tech text-[10px] uppercase tracking-widest hover:bg-sunset"
            >
              register
            </Link>

            <div className="hidden md:flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric mr-4">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-electric opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-electric" />
              </span>
              signal · locked
            </div>

            <div data-testid="console-user" className="flex items-center gap-2.5">
              <span className="h-8 w-8 rounded-full bg-mclaren/15 border border-mclaren/40 flex items-center justify-center font-mono-tech text-[11px] text-mclaren">
                {initials}
              </span>
              <span className="hidden md:inline text-sm text-white/70 max-w-[180px] truncate">
                {user?.name || user?.email}
              </span>
            </div>

            <button
              onClick={logout}
              data-testid="console-logout"
              className="group inline-flex items-center gap-2 h-9 px-4 border border-white/15 hover:border-mclaren hover:text-mclaren text-white/70 font-mono-tech text-[10px] uppercase tracking-widest transition-all"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="hidden md:inline">sign out</span>
            </button>
          </div>
        </div>
      </header>

      <section className="relative mx-auto max-w-[1500px] px-6 lg:px-10 pt-14 lg:pt-20 pb-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="chip-brand">/ showroom · world catalog</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[0.95] tracking-tighter">
            Welcome, {user?.name?.split(" ")[0] || "driver"}.
          </h1>
          <p className="mt-4 max-w-2xl text-white/60 text-base font-light leading-relaxed">
            Discover vehicles, then register one into your garage. AI agents lock to your
            registered profile - year, trim, mileage, and VIN - so you never restate ownership.
            {meta.total.toLocaleString()} catalog models across {meta.countries.length} countries.
          </p>
          {user?.role === "admin" && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/console/admin"
                className="inline-flex items-center gap-3 border border-mclaren/40 bg-mclaren/10 px-5 py-3 hover:bg-mclaren/15 transition-colors"
              >
                <Shield className="h-5 w-5 text-mclaren" />
                <span>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-mclaren block">Admin platform</span>
                  <span className="text-sm text-white/80">Users, roles, orders, full platform control</span>
                </span>
              </Link>
              <Link
                to="/console/sales"
                className="inline-flex items-center gap-3 border border-cyan-electric/40 bg-cyan-electric/10 px-5 py-3 hover:bg-cyan-electric/15 transition-colors"
              >
                <Headphones className="h-5 w-5 text-cyan-electric" />
                <span>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric block">Sales desk</span>
                  <span className="text-sm text-white/80">Live buyer leads & stock queue</span>
                </span>
              </Link>
            </div>
          )}
          {user?.role === "sales" && (
            <Link
              to="/console/sales"
              className="mt-6 inline-flex items-center gap-3 border border-cyan-electric/40 bg-cyan-electric/10 px-5 py-3 hover:bg-cyan-electric/15 transition-colors"
            >
              <Headphones className="h-5 w-5 text-cyan-electric" />
              <span>
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric block">Sales desk</span>
                <span className="text-sm text-white/80">Your workspace - leads, stock, customer chat</span>
              </span>
            </Link>
          )}
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 relative"
        >
          <div className="relative border border-white/15 bg-black/40 focus-within:border-cyan-electric transition-colors">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
            <input
              data-testid="console-search"
              type="text"
              placeholder={`Search ${meta.total.toLocaleString()} vehicles: Porsche 911, Model S, GT-R, Land Cruiser...`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-transparent pl-12 pr-32 h-14 text-white placeholder-white/30 focus:outline-none font-mono-tech text-sm tracking-wide"
              autoFocus
            />
            <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-3">
              {(q || country || brand) && (
                <button
                  onClick={clearFilters}
                  data-testid="console-clear"
                  className="text-white/40 hover:text-mclaren transition-colors"
                  aria-label="clear filters"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                {loading ? "…" : `${cars.length}${reachedEnd ? "" : "+"} / ${meta.total.toLocaleString()}`}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Active filter pills */}
        {(country || brand) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {country && (
              <ActivePill label={`Country · ${country}`} onClear={() => setCountry(null)} testId="active-country" />
            )}
            {brand && (
              <ActivePill label={`Brand · ${brand}`} onClear={() => { setBrand(null); setBrandQuery(""); }} testId="active-brand" />
            )}
          </div>
        )}

        {/* Filter row: brand autocomplete + country chips */}
        <div className="mt-6 flex flex-col lg:flex-row gap-6 lg:items-start">
          {/* Brand autocomplete */}
          <div className="relative w-full lg:w-80 shrink-0">
            <button
              onClick={() => setBrandOpen((o) => !o)}
              data-testid="brand-picker-trigger"
              className="w-full h-11 px-4 border border-white/15 hover:border-white/40 flex items-center justify-between bg-black/30 font-mono-tech text-[11px] uppercase tracking-widest text-white/70"
            >
              <span className="flex items-center gap-2">
                <Car className="h-3.5 w-3.5" strokeWidth={1.5} />
                {brand ? brand : `Brand · ${meta.brands.length}`}
              </span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${brandOpen ? "rotate-180" : ""}`} />
            </button>
            {brandOpen && (
              <div
                data-testid="brand-picker"
                className="absolute z-40 mt-2 w-full max-h-96 border border-white/15 bg-obsidian/95 backdrop-blur-xl overflow-hidden shadow-2xl flex flex-col"
              >
                <div className="p-3 border-b border-white/10 sticky top-0 bg-obsidian/95">
                  <input
                    autoFocus
                    value={brandQuery}
                    onChange={(e) => setBrandQuery(e.target.value)}
                    placeholder="Search 550 brands..."
                    data-testid="brand-picker-search"
                    className="w-full bg-black/40 border border-white/15 focus:border-cyan-electric focus:outline-none px-3 h-9 font-mono-tech text-xs text-white placeholder-white/30 transition-colors"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredBrandOptions.length === 0 ? (
                    <div className="p-6 text-center font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                      no matches
                    </div>
                  ) : (
                    filteredBrandOptions.map((b) => (
                      <button
                        key={b}
                        data-testid={`brand-option-${b.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => {
                          setBrand(b);
                          setBrandOpen(false);
                          setBrandQuery("");
                        }}
                        className={`block w-full text-left px-4 py-2 font-mono-tech text-xs uppercase tracking-widest hover:bg-white/5 ${
                          brand === b ? "text-cyan-electric" : "text-white/70"
                        }`}
                      >
                        {b}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Country chips */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-white/40 pr-2">
                <Filter className="h-3 w-3" strokeWidth={1.5} /> Country
              </span>
              {shownCountries.map((opt) => {
                const active = country === opt;
                return (
                  <button
                    key={opt}
                    data-testid={`filter-country-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setCountry(active ? null : opt)}
                    className={`font-mono-tech text-[10px] uppercase tracking-widest px-3 h-7 border transition-all ${
                      active
                        ? "border-cyan-electric text-cyan-electric bg-cyan-electric/5"
                        : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
              {meta.countries.length > topCountries.length && (
                <button
                  data-testid="filter-country-toggle"
                  onClick={() => setShowAllCountries((v) => !v)}
                  className="font-mono-tech text-[10px] uppercase tracking-widest px-3 h-7 border border-white/25 text-white/80 hover:border-cyan-electric hover:text-cyan-electric transition-all"
                >
                  {showAllCountries ? "less" : `+${meta.countries.length - topCountries.length} more`}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1500px] px-6 lg:px-10 pb-24 lg:pb-32">
        {loading && cars.length === 0 ? (
          <div className="py-20 flex items-center justify-center font-mono-tech text-[11px] uppercase tracking-widest text-white/40">
            decoding catalog...
          </div>
        ) : cars.length === 0 ? (
          <div data-testid="console-empty" className="py-20 text-center">
            <div className="font-display text-2xl text-white/70">No matches.</div>
            <div className="mt-2 text-white/40 text-sm">Try a different search term or clear filters.</div>
          </div>
        ) : (
          <>
            <div
              data-testid="console-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6"
            >
              {cars.map((c, i) => (
                <CarCard key={c.wd_id || `${c.brand}-${c.model}-${i}`} car={c} index={i % PAGE} />
              ))}
            </div>

            {/* Infinite scroll sentinel + load status */}
            <div ref={sentinelRef} className="mt-12 flex items-center justify-center h-16">
              {loadingMore ? (
                <div className="flex items-center gap-3 font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> streaming more vehicles
                </div>
              ) : reachedEnd ? (
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/30">
                  ▸ end of feed · {cars.length.toLocaleString()} decoded
                </div>
              ) : (
                <div className="h-1 w-16 bg-white/10" />
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function ActivePill({ label, onClear, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-2 h-7 pl-3 pr-2 border border-cyan-electric/40 bg-cyan-electric/5 text-cyan-electric font-mono-tech text-[10px] uppercase tracking-widest"
    >
      {label}
      <button onClick={onClear} className="hover:text-white" aria-label="clear filter">
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
